/**
 * User Role Assignment Component
 * Manages role assignments, editable permissions, and tenant access for a user.
 * Permissions tab allows direct checkbox editing — overrides are computed on save.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Paper,
  Tooltip,
  Divider,
  Checkbox,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SecurityIcon from '@mui/icons-material/Security';
import CheckIcon from '@mui/icons-material/Check';
import BusinessIcon from '@mui/icons-material/Business';
import PublicIcon from '@mui/icons-material/Public';
import FormControlLabel from '@mui/material/FormControlLabel';
import { apiGet, apiPost, apiPut, apiDelete } from '../../utils/apiClient';
import ConfirmDialog from '../common/ConfirmDialog';
import {
  ACTIONS,
  ACTION_LABELS,
  MODULE_TREE,
  MODULE_CATEGORIES,
} from '../../contexts/PermissionConstants';
import { useTenant } from '../../contexts/TenantContext';
import { useNotification } from '../../contexts/NotificationContext';
import {
  PermTreeRow,
  collectAllPaths,
  getActionState,
  filterTreeByTenant,
  collectLeafPaths,
} from './RolePermissionMatrix';

const ACTION_KEYS = Object.values(ACTIONS);

const UserRoleAssignment = ({ userId, userEmail, userTenants: initialUserTenants, onUpdate }) => {
  const [userRoles, setUserRoles] = useState([]);
  const [userTenants, setUserTenants] = useState(initialUserTenants || []);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [allTenants, setAllTenants] = useState([]);
  const [resolvedPermissions, setResolvedPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const notify = useNotification();

  // Dialog states
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [tenantDialogOpen, setTenantDialogOpen] = useState(false);
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [tenantAssignment, setTenantAssignment] = useState({
    tenant_id: '',
    is_admin: false,
  });
  const [activeTab, setActiveTab] = useState(0);
  const [actionLoading, setActionLoading] = useState(false);
  const [removeConfirm, setRemoveConfirm] = useState({ open: false, type: null, id: null, name: null });

  // Editable permissions state
  const [editPermissions, setEditPermissions] = useState({});
  const [originalPermissions, setOriginalPermissions] = useState({});
  const [permExpanded, setPermExpanded] = useState({});
  const [permSaving, setPermSaving] = useState(false);

  const { isModuleEnabled, isFeatureEnabled } = useTenant();

  // Filter tree by tenant config
  const visibleTree = useMemo(
    () => filterTreeByTenant(MODULE_TREE, isModuleEnabled, isFeatureEnabled),
    [isModuleEnabled, isFeatureEnabled]
  );

  // Build all visible paths for initialization
  const allVisiblePaths = useMemo(() => {
    const paths = [];
    for (const [code, node] of Object.entries(visibleTree)) {
      paths.push(...collectAllPaths(node, code));
    }
    return paths;
  }, [visibleTree]);

  // Group visible modules by category for permissions tab
  const categories = useMemo(() => {
    return Object.entries(MODULE_CATEGORIES).map(([catKey, moduleCodes]) => ({
      key: catKey,
      label: catKey === 'business' ? 'Business Functions' : 'Administration',
      modules: moduleCodes.filter((code) => visibleTree[code]),
    }));
  }, [visibleTree]);

  // Initialize editable permissions from resolved permissions
  const initEditablePermissions = useCallback((resolved) => {
    const fullPerms = {};
    for (const path of allVisiblePaths) {
      let source = resolved[path];
      if (!source) {
        const parts = path.split('.');
        for (let i = parts.length - 1; i > 0; i--) {
          const ancestor = parts.slice(0, i).join('.');
          if (resolved[ancestor]) {
            source = resolved[ancestor];
            break;
          }
        }
      }
      fullPerms[path] = {
        read: source?.read || false,
        create: source?.create || false,
        update: source?.update || false,
        delete: source?.delete || false,
        approve: source?.approve || false,
      };
    }

    setEditPermissions(fullPerms);
    setOriginalPermissions(JSON.parse(JSON.stringify(fullPerms)));

    // Auto-expand modules that have any permissions
    const initialExpanded = {};
    for (const code of Object.keys(visibleTree)) {
      if (ACTION_KEYS.some((a) => fullPerms[code]?.[a])) {
        initialExpanded[code] = true;
      }
    }
    setPermExpanded(initialExpanded);
  }, [allVisiblePaths, visibleTree]);

  const loadData = useCallback(async (showSpinner = true) => {
    if (!userId) return;

    try {
      if (showSpinner) setLoading(true);

      const rolesResponse = await apiGet(`/admin/users/${userId}/roles`);
      setUserRoles(rolesResponse.data || []);

      const allRolesResponse = await apiGet('/admin/roles');
      setAvailableRoles(allRolesResponse.data || []);

      try {
        const tenantsResponse = await apiGet('/tenants');
        if (tenantsResponse.success && tenantsResponse.data) {
          setAllTenants(tenantsResponse.data);
        }
      } catch {
        setAllTenants([]);
      }

      const resolvedResponse = await apiGet(`/admin/users/${userId}/resolved-permissions`);
      if (resolvedResponse.success && resolvedResponse.data) {
        const perms = resolvedResponse.data.permissions || resolvedResponse.data || {};
        setResolvedPermissions(perms);
      }
    } catch (err) {
      notify.error('Failed to load role data: ' + err.message);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- notify is stable
  }, [userId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Re-initialize editable permissions when resolved permissions change
  useEffect(() => {
    if (Object.keys(resolvedPermissions).length > 0 || !loading) {
      initEditablePermissions(resolvedPermissions);
    }
  }, [resolvedPermissions, initEditablePermissions, loading]);

  const handleAssignRole = async () => {
    if (!selectedRoleId) return;
    try {
      setActionLoading(true);
      await apiPost(`/admin/users/${userId}/roles`, { role_id: parseInt(selectedRoleId) });
      setRoleDialogOpen(false);
      setSelectedRoleId('');
      loadData(false);
      if (onUpdate) onUpdate();
    } catch (err) {
      notify.error('Failed to assign role: ' + err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRemoveRole = async (roleId) => {
    try {
      setActionLoading(true);
      await apiDelete(`/admin/users/${userId}/roles/${roleId}`);
      loadData(false);
      if (onUpdate) onUpdate();
    } catch (err) {
      notify.error('Failed to remove role: ' + err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleAssignTenant = async () => {
    if (!tenantAssignment.tenant_id) return;
    try {
      setActionLoading(true);
      const tenantId = parseInt(tenantAssignment.tenant_id);
      await apiPost(`/admin/users/${userId}/tenants`, {
        tenant_id: tenantId,
        is_admin: tenantAssignment.is_admin,
      });
      const tenant = allTenants.find(t => t.id === tenantId);
      if (tenant) {
        setUserTenants(prev => [...prev, {
          tenant_id: tenantId,
          tenant_name: tenant.name,
          tenant_code: tenant.code,
          is_admin: tenantAssignment.is_admin,
        }]);
      }
      setTenantDialogOpen(false);
      setTenantAssignment({ tenant_id: '', is_admin: false });
      if (onUpdate) onUpdate();
    } catch (err) {
      notify.error('Failed to assign tenant: ' + err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRemoveTenant = async (tenantId) => {
    try {
      setActionLoading(true);
      await apiDelete(`/admin/users/${userId}/tenants/${tenantId}`);
      setUserTenants(prev => prev.filter(t => t.tenant_id !== tenantId));
      if (onUpdate) onUpdate();
    } catch (err) {
      notify.error('Failed to remove tenant: ' + err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const getUnassignedRoles = () => {
    const assignedRoleIds = userRoles.map((r) => r.role_id);
    return availableRoles.filter((r) => !assignedRoleIds.includes(r.id));
  };

  const getAvailableTenants = () => {
    const assignedTenantIds = userTenants.map((t) => t.tenant_id);
    return allTenants.filter((t) => !assignedTenantIds.includes(t.id));
  };

  // Permission editing handlers
  const handleTogglePermExpand = useCallback((path) => {
    setPermExpanded((prev) => ({ ...prev, [path]: !prev[path] }));
  }, []);

  const handleToggleAction = useCallback((node, path, action) => {
    setEditPermissions((prev) => {
      const updated = { ...prev };
      const currentState = getActionState(node, path, action, prev);
      const newValue = currentState !== 'checked';

      const allPaths = collectAllPaths(node, path);
      for (const p of allPaths) {
        if (!updated[p]) {
          updated[p] = { read: false, create: false, update: false, delete: false, approve: false };
        }
        updated[p] = { ...updated[p], [action]: newValue };
      }
      return updated;
    });
  }, []);

  const handleSelectAllAction = useCallback((action) => {
    setEditPermissions((prev) => {
      const allLeaves = [];
      for (const [code, node] of Object.entries(visibleTree)) {
        allLeaves.push(...collectLeafPaths(node, code));
      }
      const allEnabled = allLeaves.every((p) => prev[p]?.[action]);
      const newValue = !allEnabled;

      const updated = { ...prev };
      for (const path of allVisiblePaths) {
        if (!updated[path]) {
          updated[path] = { read: false, create: false, update: false, delete: false, approve: false };
        }
        updated[path] = { ...updated[path], [action]: newValue };
      }
      return updated;
    });
  }, [visibleTree, allVisiblePaths]);

  const permHasChanges = () => {
    return JSON.stringify(editPermissions) !== JSON.stringify(originalPermissions);
  };

  const handleSavePermissions = async () => {
    try {
      setPermSaving(true);

      const filtered = { ...editPermissions };

      await apiPut(`/admin/users/${userId}/permissions`, { permissions: filtered });
      loadData(false);
      if (onUpdate) onUpdate();
    } catch (err) {
      notify.error('Failed to save permissions: ' + err.message);
    } finally {
      setPermSaving(false);
    }
  };

  const handleResetPermissions = () => {
    setEditPermissions(JSON.parse(JSON.stringify(originalPermissions)));
  };

  const handleExpandAll = useCallback(() => {
    const allExpanded = {};
    for (const path of allVisiblePaths) {
      allExpanded[path] = true;
    }
    setPermExpanded(allExpanded);
  }, [allVisiblePaths]);

  const handleCollapseAll = useCallback(() => {
    setPermExpanded({});
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box>
      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(e, v) => setActiveTab(v)}
          indicatorColor="primary"
        >
          <Tab icon={<SecurityIcon />} iconPosition="start" label="Roles" />
          <Tab icon={<BusinessIcon />} iconPosition="start" label="Tenants" />
          <Tab icon={<CheckIcon />} iconPosition="start" label="Permissions" />
        </Tabs>
      </Paper>

      {/* Assigned Roles Tab */}
      {activeTab === 0 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle2">
              Roles assigned to this user
            </Typography>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setRoleDialogOpen(true)}
              disabled={getUnassignedRoles().length === 0}
            >
              Assign Role
            </Button>
          </Box>

          {userRoles.length === 0 ? (
            <Alert severity="info">
              No roles assigned. User has no permissions.
            </Alert>
          ) : (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {userRoles.map((role) => (
                <Chip
                  key={role.role_id}
                  icon={role.is_system_role || role.tenant_id === null ? <PublicIcon /> : <SecurityIcon />}
                  label={
                    <span>
                      {role.name}
                      {(role.is_system_role || role.tenant_id === null) && ' (Global)'}
                    </span>
                  }
                  color={role.is_system_role ? 'warning' : 'primary'}
                  onDelete={() => setRemoveConfirm({ open: true, type: 'role', id: role.role_id, name: role.name })}
                  deleteIcon={
                    <Tooltip title="Remove role">
                      <DeleteIcon />
                    </Tooltip>
                  }
                />
              ))}
            </Box>
          )}
        </Box>
      )}

      {/* Tenants Tab */}
      {activeTab === 1 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle2">
              Tenant access for this user
            </Typography>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setTenantDialogOpen(true)}
              disabled={getAvailableTenants().length === 0}
            >
              Assign Tenant
            </Button>
          </Box>

          {userTenants.length === 0 ? (
            <Alert severity="info">
              No tenant access assigned. User cannot access any organization data.
            </Alert>
          ) : (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {userTenants.map((tenant) => (
                <Chip
                  key={tenant.tenant_id}
                  icon={<BusinessIcon />}
                  label={
                    <span>
                      {tenant.tenant_name || tenant.tenant_code}
                      {tenant.is_admin && ' (Admin)'}
                    </span>
                  }
                  color={tenant.is_admin ? 'primary' : 'default'}
                  onDelete={() => setRemoveConfirm({ open: true, type: 'tenant', id: tenant.tenant_id, name: tenant.tenant_name || tenant.tenant_code })}
                  deleteIcon={
                    <Tooltip title="Remove tenant access">
                      <DeleteIcon />
                    </Tooltip>
                  }
                />
              ))}
            </Box>
          )}
        </Box>
      )}

      {/* Editable Permissions Tab */}
      {activeTab === 2 && (
        <Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            Edit permissions directly. Changes are saved as overrides on top of role-based permissions.
          </Alert>

          {/* Expand/Collapse toolbar */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 0.5 }}>
            <Button size="small" variant="text" onClick={handleExpandAll}>
              Expand All
            </Button>
            <Button size="small" variant="text" onClick={handleCollapseAll}>
              Collapse All
            </Button>
          </Box>

          <TableContainer sx={{ maxHeight: 400 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 250 }}>Module / Feature</TableCell>
                  {ACTION_KEYS.map((action) => (
                    <TableCell
                      key={action}
                      align="center"
                      sx={{
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        width: 75,
                        '&:hover': { backgroundColor: 'action.hover' },
                      }}
                      onClick={() => handleSelectAllAction(action)}
                    >
                      {ACTION_LABELS[action]}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {categories.map((cat) => {
                  if (cat.modules.length === 0) return null;
                  return (
                    <React.Fragment key={cat.key}>
                      <TableRow>
                        <TableCell
                          colSpan={6}
                          sx={{ backgroundColor: 'action.hover', py: 0.5, borderBottom: 1, borderColor: 'divider' }}
                        >
                          <Typography variant="overline" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                            {cat.label}
                          </Typography>
                        </TableCell>
                      </TableRow>
                      {cat.modules.map((moduleCode) => (
                        <PermTreeRow
                          key={moduleCode}
                          node={visibleTree[moduleCode]}
                          path={moduleCode}
                          depth={0}
                          permissions={editPermissions}
                          onToggleAction={handleToggleAction}
                          readOnly={false}
                          expanded={permExpanded}
                          onToggleExpand={handleTogglePermExpand}
                        />
                      ))}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              {permHasChanges() && (
                <Chip
                  label="Unsaved changes"
                  color="warning"
                  size="small"
                  sx={{ mr: 1 }}
                />
              )}
              <Button
                variant="outlined"
                size="small"
                onClick={handleResetPermissions}
                disabled={!permHasChanges()}
              >
                Reset
              </Button>
            </Box>
            <Button
              variant="contained"
              onClick={handleSavePermissions}
              disabled={permSaving || !permHasChanges()}
            >
              {permSaving ? <CircularProgress size={24} /> : 'Save Permissions'}
            </Button>
          </Box>
        </Box>
      )}

      {/* Assign Role Dialog */}
      <Dialog open={roleDialogOpen} onClose={() => setRoleDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Assign Role</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Select Role</InputLabel>
            <Select
              value={selectedRoleId}
              onChange={(e) => setSelectedRoleId(e.target.value)}
              label="Select Role"
            >
              {getUnassignedRoles().map((role) => (
                <MenuItem key={role.id} value={role.id}>
                  {role.name} {(role.is_system_role || role.tenant_id === null) && '(Global)'}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRoleDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAssignRole}
            disabled={actionLoading || !selectedRoleId}
          >
            {actionLoading ? <CircularProgress size={24} /> : 'Assign'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign Tenant Dialog */}
      <Dialog open={tenantDialogOpen} onClose={() => setTenantDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Assign User to Tenant</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mt: 1, mb: 2 }}>
            Assigning <strong>{userEmail}</strong> to an organization
          </Alert>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Select Tenant</InputLabel>
            <Select
              value={tenantAssignment.tenant_id}
              onChange={(e) => setTenantAssignment({ ...tenantAssignment, tenant_id: e.target.value })}
              label="Select Tenant"
            >
              {getAvailableTenants().map((tenant) => (
                <MenuItem key={tenant.id} value={tenant.id}>
                  {tenant.name} ({tenant.code})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {getAvailableTenants().length === 0 && (
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              User is already assigned to all available tenants.
            </Typography>
          )}

          <Divider sx={{ my: 2 }} />

          <FormControlLabel
            control={
              <Checkbox
                checked={tenantAssignment.is_admin}
                onChange={(e) => setTenantAssignment({ ...tenantAssignment, is_admin: e.target.checked })}
              />
            }
            label="Tenant Administrator"
          />
          <Typography variant="caption" color="text.secondary" display="block">
            Tenant administrators can manage users and settings within their organization.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTenantDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAssignTenant}
            disabled={actionLoading || !tenantAssignment.tenant_id}
          >
            {actionLoading ? <CircularProgress size={24} /> : 'Assign'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={removeConfirm.open}
        title={removeConfirm.type === 'role' ? 'Remove Role' : 'Remove Tenant Access'}
        message={removeConfirm.type === 'role'
          ? `Are you sure you want to remove the role "${removeConfirm.name}" from this user?`
          : `Are you sure you want to remove access to "${removeConfirm.name}" for this user?`}
        confirmText="Remove"
        loading={actionLoading}
        onConfirm={async () => {
          if (removeConfirm.type === 'role') {
            await handleRemoveRole(removeConfirm.id);
          } else {
            await handleRemoveTenant(removeConfirm.id);
          }
          setRemoveConfirm({ open: false, type: null, id: null, name: null });
        }}
        onCancel={() => setRemoveConfirm({ open: false, type: null, id: null, name: null })}
      />
    </Box>
  );
};

export default UserRoleAssignment;
